import React from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import StreamlabsConnect from './StreamlabsConnect.jsx';
import StreamElementsConnect from './StreamElementsConnect.jsx';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/BandSettings.css';
import '../../styles/partial/OverlayIntegration.css';

/**
 * Band Settings → Overlay Integration. One page for every stream-overlay
 * provider a band can connect (Streamlabs via OAuth, StreamElements via a
 * pasted JWT). The page owns the header, back navigation and the
 * band-member guard; each provider renders as its own card.
 *
 * `isMember` comes from PerformerHome, which has already fetched the band and
 * checked membership before showing any settings view. Reusing it (rather
 * than re-fetching via useUserRole) means a transient fetch failure cannot
 * masquerade as "Access Denied" — which matters on the Streamlabs OAuth
 * return, where PerformerHome has already stripped `?streamlabs_connect=`
 * from the URL and the result would otherwise be lost.
 *
 * `streamlabsMessage` carries the result of that redirect
 * (`?streamlabs_connect=success|error`) through to the Streamlabs card.
 */
function OverlayIntegration({ band, isMember, onBack, streamlabsMessage }) {
  const t = useT();
  const header = (
    <div className="band-settings-header">
      <button onClick={onBack} className="band-settings-back-button" type="button" aria-label={t('bandSettings.backToBandSettingsLabel')}>
        <FaArrowLeft className="rtl-mirror" aria-hidden="true" /> {t('common.actions.back')}
      </button>
      <h2 className="band-settings-title">{t('overlay.title')}</h2>
    </div>
  );

  if (!isMember) {
    return (
      <div className="band-settings-content overlay-integration">
        {header}
        <div className="overlay-integration-access-denied">
          <h3>{t('common.errors.accessDenied')}</h3>
          <p>{t('overlay.accessDeniedMembersOnly')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="band-settings-content overlay-integration">
      {header}

      <div className="band-settings-description">
        <p>
          {t('overlay.description')}
        </p>
      </div>

      <div className="overlay-integration-providers">
        <StreamlabsConnect band={band} connectMessage={streamlabsMessage} />
        <StreamElementsConnect band={band} />
      </div>
    </div>
  );
}

export default OverlayIntegration;
